import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchApi, refreshAccessToken } from '../config/api';
import i18n from '../i18n';
import { applyLanguageLocally } from '../utils/language';

interface User {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  createdAt: string;
  roles: string[];
  isAdmin: boolean;
  /** Inhaber oder Mitglied einer Zuchtstaette (auch ohne Rolle Zuechter). */
  isBreedingStationMember: boolean;
  preferredLanguage: string;
}

/** Prüft ob der User eine bestimmte Rolle hat. */
export const hasRole = (user: User | null, role: string): boolean =>
  user?.roles.includes(role) ?? false;

/** Prüft ob der User mindestens eine der angegebenen Rollen hat. */
export const hasAnyRole = (user: User | null, roles: string[]): boolean =>
  roles.some((role) => user?.roles.includes(role) ?? false);

/**
 * Interessenten: fuer Zuechter und Admins sowie fuer jedes Mitglied einer Zuchtstaette - auch wer per
 * Einladungscode beigetreten ist und nur Hundehalter ist. Entspricht der Backend-Policy "ProspectAccess".
 */
export const canAccessProspects = (user: User | null): boolean =>
  hasAnyRole(user, ['Zuechter', 'Admin']) || (user?.isBreedingStationMember ?? false);

/** Bereich "DNA-Auswertungen": Berechtigung DnaAuswertung (Admins haben sie immer). Entspricht der Backend-Rolle DnaAuswertungOrAdmin. */
export const canAccessDna = (user: User | null): boolean => hasAnyRole(user, ['DnaAuswertung', 'Admin']);

export type LoginOutcome =
  | { status: 'success' }
  | { status: '2fa_required'; pendingToken: string }
  | { status: '2fa_setup_required'; pendingToken: string };

export interface TwoFactorSetupInfo {
  sharedKey: string;
  authenticatorUri: string;
  qrCodeImage: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  required: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  register: (
    email: string,
    password: string,
    confirmPassword: string,
    firstName: string,
    lastName: string
  ) => Promise<LoginOutcome>;
  completeTwoFactorLogin: (pendingToken: string, code: string, isRecoveryCode?: boolean) => Promise<void>;
  // Aktiviert 2FA und liefert die Recovery-Codes + das neue Token, WITHOUT es bereits anzuwenden
  // -- das Token wird erst per applyLoginToken uebernommen, nachdem der Nutzer die Codes gesehen hat.
  // Sonst wuerde isAuthenticated sofort auf true springen und die Recovery-Codes-Ansicht wegnavigieren.
  completeTwoFactorSetup: (code: string, pendingToken?: string) => Promise<{ recoveryCodes: string[]; tokenPayload: any }>;
  applyLoginToken: (tokenPayload: any) => void;
  fetchTwoFactorSetup: (pendingToken?: string) => Promise<TwoFactorSetupInfo>;
  getTwoFactorStatus: () => Promise<TwoFactorStatus>;
  disableTwoFactor: (password: string) => Promise<void>;
  regenerateRecoveryCodes: (password: string) => Promise<string[]>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Bildet das Backend-UserDto (firstName/lastName/fullName/displayName) auf das
// im Frontend genutzte User-Shape ab, inkl. eines einzelnen Anzeige-"name"-Felds.
const mapUserDto = (dto: any): User => {
  const firstName = dto.firstName ?? '';
  const lastName = dto.lastName ?? '';
  const fullName = dto.fullName || `${firstName} ${lastName}`.trim();
  const roles: string[] = dto.roles ?? (dto.isAdmin ? ['Admin'] : []);
  return {
    id: dto.id,
    email: dto.email ?? '',
    firstName,
    lastName,
    displayName: dto.displayName ?? undefined,
    name: dto.displayName || fullName || dto.email || '',
    createdAt: dto.createdAt ?? '',
    roles,
    isAdmin: dto.isAdmin ?? roles.includes('Admin'),
    isBreedingStationMember: dto.isBreedingStationMember ?? false,
    preferredLanguage: dto.preferredLanguage ?? 'de'
  };
};

const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it', 'nl'];

// Uebernimmt die im Konto gespeicherte Sprachpraeferenz nach Login/Refresh - der Server
// gewinnt gegenueber einer evtl. abweichenden lokalen (vor-Login) Vermutung.
const syncLanguageFromUser = (mappedUser: User) => {
  if (SUPPORTED_LANGUAGES.includes(mappedUser.preferredLanguage) && mappedUser.preferredLanguage !== i18n.language) {
    applyLanguageLocally(mappedUser.preferredLanguage as any);
  }
};

const bearerHeader = (token?: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if token exists on mount and validate it
    const storedToken = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('auth_user') || localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        syncLanguageFromUser(parsedUser);

        // Persist in canonical keys used by API helper/context
        localStorage.setItem('auth_token', storedToken);
        localStorage.setItem('auth_user', JSON.stringify(parsedUser));

        // Der gecachte Nutzer stammt vom letzten Login - Rollen koennen sich seither geaendert
        // haben (z.B. "Zuechter" durch Admin vergeben). Im Hintergrund frisch vom Server holen.
        fetchApi('/auth/me')
          .then((data) => {
            const payload = data?.data ?? data;
            if (!payload) return;
            const mappedUser = mapUserDto(payload);
            setUser(mappedUser);
            localStorage.setItem('auth_user', JSON.stringify(mappedUser));
            // Die Rollen stecken auch im Access-Token (Claims). Haben sie sich geaendert, sofort ein
            // neues Token holen - sonst antwortet z.B. /prospects bis zum Token-Ablauf mit 403.
            const oldRoles = [...(parsedUser.roles ?? [])].sort().join(',');
            if ([...mappedUser.roles].sort().join(',') !== oldRoles) {
              return refreshAccessToken().then((newToken) => {
                if (newToken) setToken(newToken);
              });
            }
          })
          .catch(() => {
            // Best-effort: bei Fehlern bleibt der gecachte Nutzer aktiv.
          });
      } catch (e) {
        console.error('Failed to parse stored user data', e);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const applyToken = (tokenPayload: any) => {
    const accessToken = tokenPayload.accessToken ?? tokenPayload.token;
    const authUser = tokenPayload.user;
    if (!accessToken || !authUser) {
      throw new Error('Invalid login response');
    }
    const mappedUser = mapUserDto(authUser);
    setToken(accessToken);
    setUser(mappedUser);
    localStorage.setItem('auth_token', accessToken);
    localStorage.setItem('auth_user', JSON.stringify(mappedUser));
    syncLanguageFromUser(mappedUser);
    if (tokenPayload.refreshToken) {
      localStorage.setItem('auth_refresh_token', tokenPayload.refreshToken);
    }
  };

  // Login/Register liefern entweder ein volles Token, oder verlangen einen zweiten
  // Faktor (Code-Eingabe oder erzwungenes Einrichten), bevor der Login abgeschlossen ist.
  const handleLoginResponse = (data: any): LoginOutcome => {
    const payload = data?.data ?? data;
    if (!payload) {
      throw new Error('Login failed');
    }

    if (payload.requiresTwoFactor && payload.pendingToken) {
      return { status: '2fa_required', pendingToken: payload.pendingToken };
    }
    if (payload.requiresTwoFactorSetup && payload.pendingToken) {
      return { status: '2fa_setup_required', pendingToken: payload.pendingToken };
    }

    applyToken(payload.token ?? payload);
    return { status: 'success' };
  };

  const login = async (email: string, password: string): Promise<LoginOutcome> => {
    try {
      const data = await fetchApi('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      return handleLoginResponse(data);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    confirmPassword: string,
    firstName: string,
    lastName: string
  ): Promise<LoginOutcome> => {
    try {
      const data = await fetchApi('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, confirmPassword, firstName, lastName }),
      });
      return handleLoginResponse(data);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const completeTwoFactorLogin = async (pendingToken: string, code: string, isRecoveryCode = false) => {
    const data = await fetchApi('/auth/2fa/verify', {
      method: 'POST',
      headers: bearerHeader(pendingToken),
      body: JSON.stringify({ code, isRecoveryCode }),
    });
    const payload = data?.data ?? data;
    applyToken(payload);
  };

  const completeTwoFactorSetup = async (
    code: string,
    pendingToken?: string
  ): Promise<{ recoveryCodes: string[]; tokenPayload: any }> => {
    const data = await fetchApi('/auth/2fa/enable', {
      method: 'POST',
      headers: bearerHeader(pendingToken),
      body: JSON.stringify({ code, isRecoveryCode: false }),
    });
    const payload = data?.data ?? data;
    return { recoveryCodes: payload.recoveryCodes ?? [], tokenPayload: payload.token };
  };

  const fetchTwoFactorSetup = async (pendingToken?: string): Promise<TwoFactorSetupInfo> => {
    const data = await fetchApi('/auth/2fa/setup', {
      method: 'POST',
      headers: bearerHeader(pendingToken),
    });
    return data?.data ?? data;
  };

  const getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
    const data = await fetchApi('/auth/2fa/status');
    return data?.data ?? data;
  };

  const disableTwoFactor = async (password: string) => {
    await fetchApi('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  };

  const regenerateRecoveryCodes = async (password: string): Promise<string[]> => {
    const data = await fetchApi('/auth/2fa/recovery-codes', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    return data?.data ?? data ?? [];
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('auth_refresh_token');

    // Best-effort: das Refresh-Token serverseitig widerrufen, damit es nicht
    // weiterverwendet werden kann. fetchApi liest den Access-Token synchron
    // (vor dem ersten await), daher muss dieser Aufruf VOR dem localStorage-
    // Cleanup unten stehen, obwohl er selbst fire-and-forget ist.
    if (refreshToken) {
      fetchApi('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  };

  const refreshUser = async () => {
    const data = await fetchApi('/auth/me');
    const payload = data?.data ?? data;
    if (!payload) return;
    const mappedUser = mapUserDto(payload);
    const oldRoles = [...(user?.roles ?? [])].sort().join(',');
    setUser(mappedUser);
    localStorage.setItem('auth_user', JSON.stringify(mappedUser));
    syncLanguageFromUser(mappedUser);
    // Rollen stecken auch im Access-Token - bei Aenderung (z.B. Admin vergibt sich selbst eine
    // Rolle) sofort ein neues Token holen, sonst greifen die neuen Rechte erst nach Token-Ablauf.
    if ([...mappedUser.roles].sort().join(',') !== oldRoles) {
      const newToken = await refreshAccessToken();
      if (newToken) setToken(newToken);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        login,
        register,
        completeTwoFactorLogin,
        completeTwoFactorSetup,
        applyLoginToken: applyToken,
        fetchTwoFactorSetup,
        getTwoFactorStatus,
        disableTwoFactor,
        regenerateRecoveryCodes,
        logout,
        refreshUser,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
